/**
 * ProfilePageRedesign Component
 * 
 * Features:
 * - Collapsible sections with progress indicators
 * - Section-level save buttons (header + bottom)
 * - Navigation guards for unsaved changes
 * - Automatic browser beforeunload protection
 * 
 * Navigation Guard Usage:
 * To integrate with React Router, you can use the checkUnsavedChanges function:
 * 
 * Example with React Router v6:
 * ```jsx
 * import { useBlocker } from 'react-router-dom';
 * 
 * const ProfilePageRedesign = () => {
 *   // ... existing code ...
 *   
 *   useBlocker(
 *     ({ currentLocation, nextLocation }) =>
 *       hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
 *   );
 * ```
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  LinearProgress,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton } from '@mui/material';
import { Security,
  ArrowBack,
  ChevronRight,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { logAnalyticsEvent } from '../../../utils/analytics';
import useCheckoutSuccessConversion from '../../../hooks/useCheckoutSuccessConversion';
import { MedicationStackService } from '../../../services/medicationStackService';
import { DietStackService } from '../../../services/dietStackService';
import dayjs from 'dayjs';
import BasicInfoSection from './BasicInfoSection';
import MedicationList from './MedicationList';
import AccountSubscriptionSection from './AccountSubscriptionSection';
import { useAuth } from '../../../AuthContext';
import { alpha } from '@mui/material/styles';
import { color, radius, r } from '../../../theme/tokens';

const ProfilePageRedesign = () => {
  const { currentUser, subscriptionStatus } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const successTimeoutRef = useRef(null);
  const [unsavedSections, setUnsavedSections] = useState(new Set());
  
  // Computed state
  const hasUnsavedChanges = unsavedSections.size > 0;
  
  // Check URL params for section to slide in
  const [currentSection, setCurrentSection] = useState(null); // Track which section is slid in
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null); // Track where user wants to navigate

  const [formData, setFormData] = useState({
    basicInfo: {
      firstName: '',
      lastName: '',
      email: '',
      dateOfBirth: '',
      gender: '',
      dietPreference: '',
      lifestage: { pregnant: false, breastfeeding: false },
    },
    medications: [],
  });

  // Store original data for canceling changes
  const [originalData, setOriginalData] = useState({
    basicInfo: {
      firstName: '',
      lastName: '',
      email: '',
      dateOfBirth: '',
      gender: '',
      dietPreference: '',
      lifestage: { pregnant: false, breastfeeding: false },
    },
    medications: [],
  });

  const [progress, setProgress] = useState({
    basicInfo: 0,
    medications: 0,
    security: 100, // Assuming security is always "complete" for now
    overall: 0,
  });

  const calculateProgress = useCallback((data) => {
    // Basic Info Progress - include dietPreference as optional
    const requiredFields = ['firstName', 'lastName', 'dateOfBirth', 'gender'];
    const optionalFields = ['dietPreference'];
    
    const filledRequiredFields = requiredFields.filter(field => !!data.basicInfo[field]);
    const filledOptionalFields = optionalFields.filter(field => !!data.basicInfo[field]);
    
    // Basic info: 80% for required fields, 20% for optional fields
    const requiredProgress = (filledRequiredFields.length / requiredFields.length) * 80;
    const optionalProgress = (filledOptionalFields.length / optionalFields.length) * 20;
    const basicInfoProgress = requiredProgress + optionalProgress;

    // Medications Progress - 100% if at least one med, 0% otherwise
    const medicationsProgress = data.medications && data.medications.length > 0 ? 100 : 0;
    
    // Security is always 100% (account exists)
    const securityProgress = 100;
    
    // Overall Progress (weighted average)
    const overall = (basicInfoProgress * 0.6) + (medicationsProgress * 0.2) + (securityProgress * 0.2);

    setProgress({
      basicInfo: Math.round(basicInfoProgress),
      medications: medicationsProgress,
      security: securityProgress,
      overall: Math.round(overall),
    });
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      if (currentUser) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const medProfileDocRef = doc(db, 'med_profile', currentUser.uid);

        try {
          const [userDoc, medProfileDoc] = await Promise.all([
            getDoc(userDocRef),
            getDoc(medProfileDocRef),
          ]);

          const newFormData = { ...formData };

          if (userDoc.exists()) {
            const userData = userDoc.data();
            newFormData.basicInfo = {
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              email: userData.email || '',
              dateOfBirth: userData.dateOfBirth || '',
              gender: userData.gender || '',
              dietPreference: userData.dietPreference || '',
              lifestage: userData.lifestage || { pregnant: false, breastfeeding: false },
            };
          }

          if (medProfileDoc.exists()) {
            const medProfileData = medProfileDoc.data();
            newFormData.medications = medProfileData.medications || [];
          }
          
          setFormData(newFormData);
          setOriginalData(newFormData); // Store original data for canceling
          calculateProgress(newFormData);

        } catch (err) {
          console.error("Error fetching user data:", err);
          setError("Failed to load your profile. Please try refreshing the page.");
        }
      }
      setLoading(false);
    };
    fetchUserData();
  }, [currentUser, calculateProgress]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = ''; // Required for Chrome
        return 'You have unsaved changes. Are you sure you want to leave this page?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Update page title to indicate unsaved changes
  useEffect(() => {
    const originalTitle = document.title;
    if (hasUnsavedChanges) {
      if (!document.title.startsWith('• ')) {
        document.title = `• ${document.title}`;
      }
    } else {
      if (document.title.startsWith('• ')) {
        document.title = document.title.substring(2);
      }
    }

    // Cleanup on unmount
    return () => {
      document.title = originalTitle;
    };
  }, [hasUnsavedChanges]);

  // Record a completed Stripe checkout (Mixpanel + the Google Ads conversion).
  useCheckoutSuccessConversion(searchParams, setSearchParams, subscriptionStatus);

  // Handle URL parameter changes for section sliding
  useEffect(() => {
    const sectionParam = searchParams.get('section');

    // Handle backward compatibility for old section names
    if (sectionParam === 'security' || sectionParam === 'subscription') {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('section', 'account-subscription');
      setSearchParams(newParams, { replace: true });
      return;
    }

    if (sectionParam && ['basicInfo', 'medications', 'account-subscription'].includes(sectionParam)) {
      setCurrentSection(sectionParam);
    } else if (!sectionParam) {
      setCurrentSection(null);
    }
  }, [searchParams, setSearchParams]);

  const handleSectionClick = (sectionName) => {
    setCurrentSection(sectionName);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('section', sectionName);
    setSearchParams(newParams, { replace: true });
  };

  // Common navigation handling with unsaved changes protection
  const handleNavigation = (destination) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(destination);
      setShowUnsavedDialog(true);
    } else {
      executeNavigation(destination);
    }
  };

  const executeNavigation = (destination) => {
    if (destination === 'main') {
      setCurrentSection(null);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('section');
      setSearchParams(newParams, { replace: true });
    } else {
      // Handle other navigation (dashboard, etc.)
      // This can be extended for other destinations
    }
  };

  const handleBackToMain = () => {
    handleNavigation('main');
  };

  const handleConfirmNavigation = () => {
    if (pendingNavigation) {
      executeNavigation(pendingNavigation);
    }
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };

  const handleSaveAndNavigate = async () => {
    try {
      setIsSaving(true);
      await handleSave();
      if (pendingNavigation) {
        executeNavigation(pendingNavigation);
      }
      setShowUnsavedDialog(false);
      setPendingNavigation(null);
    } catch (error) {
      console.error('Error saving before navigation:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Common cancel function for all sections
  const handleCancelSection = (sectionName) => {
    // Reset to original saved data
    setFormData(prev => {
      const restoredData = {
        ...prev,
        [sectionName]: originalData[sectionName]
      };
      calculateProgress(restoredData);
      return restoredData;
    });
    setUnsavedSections(prev => {
      const newSet = new Set(prev);
      newSet.delete(sectionName);
      return newSet;
    });
  };

  const handleCancelSectionAndExit = (sectionName) => {
    if (unsavedSections.has(sectionName)) {
      handleCancelSection(sectionName);
    }
    executeNavigation('main');
  };

  const handleSaveSectionAndExit = async (saveHandler) => {
    const saved = await saveHandler();
    if (saved) {
      executeNavigation('main');
    }
  };

  // Common save/cancel button component
  const renderSectionButtons = (sectionName, saveHandler) => {
    const hasUnsavedSectionChanges = unsavedSections.has(sectionName);

    return (
      <Box sx={{
        p: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: `blur(${r(20)})`,
        borderBottom: `${r(0.5)} solid rgba(0, 0, 0, 0.1)`
      }}>
        <Box sx={{ display: 'flex', gap: 2, maxWidth: 860, mx: 'auto' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleCancelSectionAndExit(sectionName)}
            disabled={isSaving}
            sx={{
              borderColor: color.line,
              color: color.inkSoft,
              borderRadius: radius.md,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': { 
                borderColor: color.inkMuted,
                backgroundColor: alpha(color.inkMuted, 0.1)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => handleSaveSectionAndExit(saveHandler)}
            disabled={isSaving || !hasUnsavedSectionChanges}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              backgroundColor: color.accent,
              color: color.accentInk,
              borderRadius: radius.md,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: color.accentHover },
              '&:disabled': { backgroundColor: alpha(color.accent, 0.3) }
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    );
  };

  // Handle changes from child components
  const handleBasicInfoChange = (updatedBasicInfo) => {
    setFormData(prev => ({ ...prev, basicInfo: updatedBasicInfo }));
    calculateProgress({ ...formData, basicInfo: updatedBasicInfo });
    setUnsavedSections(prev => new Set([...prev, 'basicInfo']));
  };

  const handleMedicationChange = (updatedMedications) => {
    setFormData(prev => ({ ...prev, medications: updatedMedications }));
    calculateProgress({ ...formData, medications: updatedMedications });
    setUnsavedSections(prev => new Set([...prev, 'medications']));
  };

  // Section-specific save handlers
  const handleSaveBasicInfo = async () => {
    if (!unsavedSections.has('basicInfo')) return false;
    
    setIsSaving(true);
    setError('');
    setSuccess('');
    
    const user = currentUser;
    if (!user) {
      setError('You must be logged in to save your profile.');
      setIsSaving(false);
      return false;
    }
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const dataToSave = {
        ...formData.basicInfo,
        updatedAt: new Date().toISOString(),
      };
      console.log('💾 [ProfilePageRedesign] Saving basic info data:', dataToSave);
      await updateDoc(userDocRef, dataToSave);
      
      // Update original data after successful save
      setOriginalData(prev => ({
        ...prev,
        basicInfo: formData.basicInfo
      }));

      setSuccess('Basic information saved successfully!');
      
      // Auto-clear success message after 2 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess('');
      }, 2000);
      
      setUnsavedSections(prev => {
        const newSet = new Set(prev);
        newSet.delete('basicInfo');
        return newSet;
      });

      // Generate diet support stack if user has diet preferences
      if (formData.basicInfo.dietPreference && formData.basicInfo.dietPreference !== '') {
        try {
          const userProfile = {
            age: formData.basicInfo.dateOfBirth ? dayjs().diff(dayjs(formData.basicInfo.dateOfBirth), 'year') : 25,
            sex: formData.basicInfo.gender === 'male' ? 'Male' : formData.basicInfo.gender === 'female' ? 'Female' : 'Other',
            lifestage: formData.basicInfo.lifestage || { pregnant: false, breastfeeding: false },
            diet: formData.basicInfo.dietPreference
          };
          
          console.log('🥗 [ProfilePageRedesign] Generating diet support stack with profile:', userProfile);
          await DietStackService.generateStack(userProfile);
          console.log('🥗 [ProfilePageRedesign] Diet support stack generation triggered successfully');
        } catch (dietError) {
          console.error('❌ [ProfilePageRedesign] Failed to generate diet support stack:', dietError);
          // Don't show error to user as this is a background operation
        }
      }
      return true;
      
    } catch (err) {
      setError('Failed to save basic information. Please try again.');
      console.error(err);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMedications = async () => {
    if (!currentUser || !formData.medications) return false;
    
    setIsSaving(true);
    setError('');
    setSuccess('');
    
    const user = currentUser;
    if (!user) {
      setError('You must be logged in to save your profile.');
      setIsSaving(false);
      return false;
    }
    
    try {
      const medProfileDocRef = doc(db, 'med_profile', user.uid);
      // Use setDoc with merge to handle document creation gracefully
      await setDoc(medProfileDocRef, {
        medications: formData.medications,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      
      // Update original data after successful save
      setOriginalData(prev => ({
        ...prev,
        medications: formData.medications
      }));

      setSuccess('Medications saved successfully!');
      
      // Auto-clear success message after 2 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess('');
      }, 2000);
      
      setUnsavedSections(prev => {
        const newSet = new Set(prev);
        newSet.delete('medications');
        return newSet;
      });
      
      // Trigger medication support stack generation in the background
      MedicationStackService.generateStack(formData.medications).catch(error => {
        console.warn('Failed to generate medication support stack:', error);
        // Don't show this error to the user as it's a background operation
      });
      return true;
      
    } catch (err) {
      setError('Failed to save medications. Please try again.');
      console.error(err);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Navigation guard utility (optional - uncomment if you want popup confirmations)
  // const checkUnsavedChanges = () => {
  //   if (hasUnsavedChanges) {
  //     setShowUnsavedDialog(true);
  //     return false; // Prevent navigation
  //   }
  //   return true; // Allow navigation
  // };

  // const handleDiscardChanges = () => {
  //   setUnsavedSections(new Set());
  //   setShowUnsavedDialog(false);
  //   // Optionally reload the page or reset form data
  //   window.location.reload();
  // };

  // const handleSaveAndContinue = async () => {
  //   setIsSaving(true);
  //   try {
  //     // Save all unsaved sections
  //     if (unsavedSections.has('basicInfo')) {
  //       await handleSaveBasicInfo();
  //     }
  //     if (unsavedSections.has('medications')) {
  //       await handleSaveMedications();
  //     }
  //     setShowUnsavedDialog(false);
  //   } catch (error) {
  //     console.error('Error saving changes:', error);
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    
    const user = currentUser;
    if (!user) {
      setError('You must be logged in to save your profile.');
      setIsSaving(false);
      return;
    }
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const medProfileDocRef = doc(db, 'med_profile', user.uid);

      const userPromise = updateDoc(userDocRef, {
        ...formData.basicInfo,
        updatedAt: new Date().toISOString(),
      });
      
      // Use setDoc with merge to safely create/update the med_profile
      const medProfilePromise = setDoc(medProfileDocRef, {
        medications: formData.medications,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      await Promise.all([userPromise, medProfilePromise]);
      
      logAnalyticsEvent('profile_redesign_save', { 
        status: 'success',
        medication_count: formData.medications.length,
        profile_progress: progress.overall
      });
      
      setSuccess('Profile saved successfully!');
      
      // Auto-clear success message after 2 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess('');
      }, 2000);
      
      setUnsavedSections(new Set()); // Clear unsaved sections tracking
      
    } catch (err) {
      setError('Failed to save profile. Please try again.');
      logAnalyticsEvent('profile_redesign_save', { status: 'failure', error: err.message });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>Your Profile</Typography>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 4 }, 
          mb: 4, 
          background: `linear-gradient(135deg, ${alpha(color.accent, 0.15)} 0%, ${alpha(color.evergreen, 0.08)} 100%)`,
          border: `${r(1)} solid ${alpha(color.accent, 0.2)}`,
          borderRadius: 3,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: r(4),
            background: color.accent,
            borderRadius: `${radius.md} ${radius.md} 0 0`
          }
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{
            color: color.evergreen,
            fontWeight: 700,
            fontSize: { xs: '1.75rem', sm: '2rem' },
            letterSpacing: '-0.02em',
            mb: 1
          }}
        >
          Your Profile
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 3,
            color: 'text.secondary',
            fontSize: { xs: '0.95rem', sm: '1rem' },
            lineHeight: 1.6
          }}
        >
          {Math.round(progress.overall) < 100
            ? "You're almost there! Complete your profile for stacks that consider your unique diet and medications."
            : "Keeping your details current helps your recommendations evolve with you."
          }
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: '100%', mr: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress.overall} 
              sx={{ 
                height: 12, 
                borderRadius: 6,
                backgroundColor: alpha(color.accent, 0.2),
                '& .MuiLinearProgress-bar': {
                  background: color.accent,
                  borderRadius: 6
                }
              }} 
            />
          </Box>
          <Box sx={{ minWidth: 45 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: color.evergreen,
                fontSize: '0.875rem'
              }}
            >
              {`${Math.round(progress.overall)}%`}
            </Typography>
          </Box>
        </Box>

        {/* Privacy Disclaimer */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 1.5,
          px: 1.5,
          py: 1,
          backgroundColor: alpha(color.evergreen, 0.04),
          borderRadius: 1.5,
          border: `${r(1)} solid ${alpha(color.evergreen, 0.1)}`
        }}>
          <Security sx={{
            fontSize: '1rem',
            color: 'primary.main',
            mr: 1
          }} />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.3
            }}
          >
            Your information is kept private and secure
          </Typography>
        </Box>
      </Paper>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Gentle Unsaved Changes Indicator - Only on Main Screen */}
      {!currentSection && hasUnsavedChanges && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              // Neutral surface with an accent marker, not amber. Unsaved work
              // is a *state*, not a hazard — the affordance is the Save button
              // enabling in each section. Amber in this product means "these
              // medications may interact dangerously", and spending that hue on
              // housekeeping devalues a safety signal (DESIGN_DOC §10).
              backgroundColor: color.surface,
              border: `${r(1)} solid ${color.line}`,
              borderRadius: 2,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                backgroundColor: color.accent,
                borderRadius: `${r(2)} 0 0 ${r(2)}`,
              }
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  mb: 0.25,
                }}
              >
                {unsavedSections.size === 1 
                  ? 'You have unsaved changes' 
                  : `You have unsaved changes in ${unsavedSections.size} sections`
                }
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                }}
              >
                Go to each section to save your changes
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Sliding Interface Container */}
      <Box sx={{ 
        position: 'relative',
        overflow: 'hidden',
        minHeight: 600,
        borderRadius: 3,
        boxShadow: `0 ${r(4)} ${r(20)} rgba(0,0,0,0.08)`,
        backgroundColor: 'background.paper'
      }}>
        {/* Main Sections View */}
        <Box sx={{
          display: currentSection ? 'none' : 'block',
          p: 4,
          animation: 'fadeInUp 0.5s ease-out',
        '@keyframes fadeInUp': {
            '0%': { opacity: 0, transform: `translateY(${r(20)})` },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          {/* Section Cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              onClick={() => handleSectionClick('account-subscription')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 3,
                backgroundColor: 'white',
                borderRadius: radius.md,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: `0 ${r(1)} ${r(3)} rgba(0, 0, 0, 0.1)`,
                '&:hover': {
                  backgroundColor: color.surface,
                  transform: 'scale(1.02)',
                },
                '&:active': {
                  backgroundColor: color.line,
                  transform: 'scale(0.98)',
                }
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ 
                  mb: 0.5, 
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: color.ink
                }}>
                  Account & Subscription
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinearProgress
                    variant="determinate"
                    value={100}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: alpha(color.accent, 0.2),
                      '& .MuiLinearProgress-bar': {
                        background: color.accent,
                        borderRadius: 2
                      },
                      flexGrow: 1,
                      mr: 1.5,
                    }}
                  />
                  <Typography variant="caption" sx={{ 
                    fontWeight: 600, 
                    color: color.evergreen,
                    fontSize: '0.8rem'
                  }}>
                    100%
                  </Typography>
                </Box>
              </Box>
              <ChevronRight sx={{ 
                color: color.inkMuted, 
                fontSize: '1.5rem',
                ml: 1
              }} />
            </Box>

            <Box
              onClick={() => handleSectionClick('basicInfo')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 3,
                backgroundColor: 'white',
                borderRadius: radius.md,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: `0 ${r(1)} ${r(3)} rgba(0, 0, 0, 0.1)`,
                '&:hover': {
                  backgroundColor: color.surface,
                  transform: 'scale(1.02)',
                },
                '&:active': {
                  backgroundColor: color.line,
                  transform: 'scale(0.98)',
                }
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ 
                  mb: 0.5, 
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: color.ink
                }}>
                  Health Profile
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress.basicInfo}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: alpha(color.accent, 0.2),
                      '& .MuiLinearProgress-bar': {
                        background: color.accent,
                        borderRadius: 2
                      },
                      flexGrow: 1,
                      mr: 1.5,
                    }}
                  />
                  <Typography variant="caption" sx={{ 
                    fontWeight: 600, 
                    color: color.evergreen,
                    fontSize: '0.8rem'
                  }}>
                    {Math.round(progress.basicInfo)}%
                  </Typography>
                </Box>
              </Box>
              <ChevronRight sx={{ 
                color: color.inkMuted, 
                fontSize: '1.5rem',
                ml: 1
              }} />
            </Box>

            <Box
              onClick={() => handleSectionClick('medications')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 3,
                backgroundColor: 'white',
                borderRadius: radius.md,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: `0 ${r(1)} ${r(3)} rgba(0, 0, 0, 0.1)`,
                '&:hover': {
                  backgroundColor: color.surface,
                  transform: 'scale(1.02)',
                },
                '&:active': {
                  backgroundColor: color.line,
                  transform: 'scale(0.98)',
                }
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ 
                  mb: 0.5, 
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  color: color.ink
                }}>
                  Current Medications
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress.medications}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: alpha(color.accent, 0.2),
                      '& .MuiLinearProgress-bar': {
                        background: color.accent,
                        borderRadius: 2
                      },
                      flexGrow: 1,
                      mr: 1.5,
                    }}
                  />
                  <Typography variant="caption" sx={{ 
                    fontWeight: 600, 
                    color: color.evergreen,
                    fontSize: '0.8rem'
                  }}>
                    {Math.round(progress.medications)}%
                  </Typography>
                </Box>
              </Box>
              <ChevronRight sx={{ 
                color: color.inkMuted, 
                fontSize: '1.5rem',
                ml: 1
              }} />
            </Box>
          </Box>
        </Box>

        {/* Account & Subscription Slide */}
        <Box sx={{
          display: currentSection === 'account-subscription' ? 'block' : 'none',
          position: 'relative',
          backgroundColor: color.surface,
          animation: 'slideInFromRight 0.3s ease-out',
          '@keyframes slideInFromRight': {
            '0%': { transform: 'translateX(100%)' },
            '100%': { transform: 'translateX(0)' },
          },
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* iOS-style Navigation Header */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: `blur(${r(20)})`,
              borderBottom: `${r(0.5)} solid rgba(0, 0, 0, 0.1)`,
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <IconButton 
                onClick={handleBackToMain} 
                sx={{ 
                  mr: 1,
                  color: color.accent,
                  '&:hover': { backgroundColor: alpha(color.accent, 0.1) }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                fontSize: '1.125rem',
                color: color.ink
              }}>
                Account & Subscription
              </Typography>
            </Box>
            {/* Content Area */}
            <Box sx={{ 
              overflow: 'visible',
              px: 2,
              pt: 3,
              pb: 4
            }}>
          <AccountSubscriptionSection userData={formData.basicInfo} />
            </Box>
          </Box>
        </Box>

        {/* Health Profile Slide */}
        <Box sx={{
          display: currentSection === 'basicInfo' ? 'block' : 'none',
          position: 'relative',
          backgroundColor: color.surface,
          animation: 'slideInFromRight 0.3s ease-out',
          '@keyframes slideInFromRight': {
            '0%': { transform: 'translateX(100%)' },
            '100%': { transform: 'translateX(0)' },
          },
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* iOS-style Navigation Header */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: `blur(${r(20)})`,
              borderBottom: `${r(0.5)} solid rgba(0, 0, 0, 0.1)`,
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <IconButton 
                onClick={handleBackToMain} 
                sx={{ 
                  mr: 1,
                  color: color.accent,
                  '&:hover': { backgroundColor: alpha(color.accent, 0.1) }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                fontSize: '1.125rem',
                color: color.ink
              }}>
                Health Profile
              </Typography>
            </Box>
            {renderSectionButtons('basicInfo', handleSaveBasicInfo)}
            
            {/* Content Area */}
            <Box sx={{ 
              overflow: 'visible',
              pt: 3,
              pb: 4
            }}>
              {/* iOS-style Form Container */}
              <Box sx={{ px: 2, mb: 3 }}>
          <BasicInfoSection 
            initialData={formData.basicInfo}
            onSave={handleBasicInfoChange}
          />
              </Box>
            </Box>

          </Box>
        </Box>

        {/* Medications Slide */}
        <Box sx={{
          display: currentSection === 'medications' ? 'block' : 'none',
          position: 'relative',
          backgroundColor: color.surface,
          animation: 'slideInFromRight 0.3s ease-out',
          '@keyframes slideInFromRight': {
            '0%': { transform: 'translateX(100%)' },
            '100%': { transform: 'translateX(0)' },
          },
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* iOS-style Navigation Header */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: `blur(${r(20)})`,
              borderBottom: `${r(0.5)} solid rgba(0, 0, 0, 0.1)`,
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <IconButton 
                onClick={handleBackToMain} 
                sx={{ 
                  mr: 1,
                  color: color.accent,
                  '&:hover': { backgroundColor: alpha(color.accent, 0.1) }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                fontSize: '1.125rem',
                color: color.ink
              }}>
                Current Medications
              </Typography>
            </Box>
            {renderSectionButtons('medications', handleSaveMedications)}
            
            {/* Content Area */}
            <Box sx={{ 
              overflow: 'visible',
              pt: 3,
              pb: 4
            }}>
              {/* iOS-style Form Container */}
              <Box sx={{ px: 2, mb: 3 }}>
          <MedicationList
            medications={formData.medications}
            onChange={handleMedicationChange}
          />
              </Box>
            </Box>

          </Box>
        </Box>
      </Box>
      
      {/* Unsaved Changes Dialog - iOS Style */}
      <Dialog
        open={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        aria-labelledby="unsaved-dialog-title"
        aria-describedby="unsaved-dialog-description"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: radius.md,
            minWidth: r(280),
            maxWidth: r(320)
          }
        }}
      >
        <DialogTitle 
          id="unsaved-dialog-title"
          sx={{ 
            textAlign: 'center',
            fontSize: '1.125rem',
            fontWeight: 600,
            pb: 1
          }}
        >
          Save Changes?
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <DialogContentText 
            id="unsaved-dialog-description"
            sx={{ 
              textAlign: 'center',
              fontSize: '0.9rem',
              lineHeight: 1.4,
              color: 'text.secondary'
            }}
          >
            You have unsaved changes that will be lost if you continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 0, p: 0 }}>
          <Button 
            onClick={handleSaveAndNavigate}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              width: '100%',
              py: 2,
              fontSize: '1rem',
              fontWeight: 600,
              color: color.accent,
              borderTop: `${r(0.5)} solid rgba(0, 0, 0, 0.1)`,
              borderRadius: 0,
              '&:hover': { backgroundColor: alpha(color.accent, 0.1) }
            }}
          >
            {isSaving ? 'Saving...' : 'Save & Continue'}
          </Button>
          <Button 
            onClick={handleConfirmNavigation}
            sx={{
              width: '100%',
              py: 2,
              fontSize: '1rem',
              fontWeight: 400,
              // Destructive: this discards edits the user has already made, and
              // it sits between two accent alternatives that keep them. It has
              // to read as the option that loses work.
              color: color.destructive,
              borderTop: `${r(0.5)} solid rgba(0, 0, 0, 0.1)`,
              borderRadius: 0,
              '&:hover': { backgroundColor: alpha(color.destructive, 0.1) }
            }}
          >
            Don't Save
          </Button>
          <Button 
            onClick={() => setShowUnsavedDialog(false)}
            sx={{
              width: '100%',
              py: 2,
              fontSize: '1rem',
              fontWeight: 600,
              color: color.accent,
              borderTop: `${r(0.5)} solid rgba(0, 0, 0, 0.1)`,
              borderBottomLeftRadius: radius.md,
              borderBottomRightRadius: radius.md,
              '&:hover': { backgroundColor: alpha(color.accent, 0.1) }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePageRedesign; 